import _ from 'lodash';
import React from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  styled
} from '@mui/material';
import {
  activityTemplates,
  FormattedCode,
  StatusCheckIcon
} from 'modules';
import {
  LoadingOverlay,
  Section
} from 'iron-forge-react-modules';

import { useParams } from 'react-router';
import { LeaseApplicationStepNameName } from 'generated/graphql';
import * as SmartPayLeaseApplicationSection from './SmartPayLeaseApplicationSection/SmartPayLeaseApplicationSection';
import * as dataService from './dataService';



const AccordionContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3)
}));

const RowBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(4)
}));

const ColumnBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3)
}));

const TableContainerBox = styled(Box)(() => ({
  overflowY: 'scroll'
}));

const Table = styled('table')(() => ({}));

const TableCell = styled('td')(({ theme }) => ({
  padding: theme.spacing(2),
  paddingTop: theme.spacing(1)
}));



interface IParams {
  orderId: string;
}

export const OrderDetailActivity: React.FC = () => {
  const params = useParams<Partial<IParams>>();
  const queryTuple = dataService.useQuery(params.orderId ?? '');



  const fnStepIsComplete = React.useCallback(
    (step: LeaseApplicationStepNameName) =>
      !_.isNil(_.find(queryTuple.data.completedSteps, s => s === step)),
    [queryTuple]);



  return (
    <activityTemplates.BaseActivityTemplate.FC>
      <LoadingOverlay.FC active={queryTuple.loading} />

      {!queryTuple.loading && (
        <Section.FC>
          <h1>{queryTuple.data.orderNumber}</h1>

          <ColumnBox>
            <Card>
              <CardContent>
                <RowBox>
                  <Box>
                    <p>Order Status</p>
                    <b>{queryTuple.data.orderStatusDisplayName}</b>
                  </Box>

                  <Box>
                    <p>Date</p>
                    <b>{queryTuple.data.orderDate.toFormat('LLL dd yyyy @ hh:mm a')}</b>
                  </Box>
                </RowBox>

              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <h3>Customer Information</h3>

                <RowBox>
                  <Box>
                    <p><b>Customer Name</b></p>
                    <p>{queryTuple.data.customerFullName}</p>
                  </Box>

                  <Box>
                    <p><b>Customer Email</b></p>
                    <p>{queryTuple.data.customerEmail}</p>
                  </Box>
                </RowBox>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <h3>Payment Information</h3>

                <RowBox>
                  <Box>
                    <p><b>Method</b></p>
                    <p>{queryTuple.data.paymentMethodDisplayName}</p>
                  </Box>

                  {queryTuple.data.smartPayApplicationNumber && (
                    <Box>
                      <p><b>Lease Application</b></p>
                      <p>#{queryTuple.data.smartPayApplicationNumber}</p>
                    </Box>
                  )}

                  {queryTuple.data.smartPayApplicationNumber && (
                    <>
                      <Box>
                        <p><b>Application Complete</b></p>
                        <StatusCheckIcon.FC success={fnStepIsComplete('application')} />
                      </Box>

                      <Box>
                        <p><b>Cart</b></p>
                        <StatusCheckIcon.FC success={fnStepIsComplete('cart')} />
                      </Box>

                      <Box>
                        <p><b>Agreement</b></p>
                        <StatusCheckIcon.FC success={fnStepIsComplete('agreement')} />
                      </Box>

                      <Box>
                        <p><b>Funding</b></p>
                        <StatusCheckIcon.FC success={fnStepIsComplete('funding')} />
                      </Box>

                      <Box>
                        <p><b>Confirmation</b></p>
                        <StatusCheckIcon.FC success={fnStepIsComplete('confirmation')} />
                      </Box>

                      <Box>
                        <p><b>FraudCheck</b></p>
                        <StatusCheckIcon.FC success={fnStepIsComplete('fraudCheck')} />
                      </Box>

                      <Box>
                        <p><b>Lease</b></p>
                        <StatusCheckIcon.FC success={fnStepIsComplete('lease')} />
                      </Box>

                      <Box>
                        <p><b>Activated</b></p>
                        <StatusCheckIcon.FC success={fnStepIsComplete('activated')} />
                      </Box>
                    </>
                  )}
                </RowBox>
              </CardContent>
            </Card>

            {queryTuple.data.trackingNumber && (
              <Card>
                <CardContent>
                  <h3>Shipping Information</h3>

                  <RowBox>
                    <Box>
                      <p><b>Tracking Number</b></p>

                      <p>{queryTuple.data.trackingNumber}</p>
                    </Box>

                    <Box>
                      <p><b>Carrier Code</b></p>
                      <p>{queryTuple.data.carrierCode}</p>
                    </Box>
                  </RowBox>
                </CardContent>
              </Card>
            )}

            {!_.isEmpty(queryTuple.data.deviceLedgerInfo) && (
              <Card>
                <CardContent>
                  <h3>Device Ledger</h3>

                  <Table>
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>IMEI</th>
                        <th>ICCID</th>
                      </tr>
                    </thead>

                    <tbody>
                      {_.map(queryTuple.data.deviceLedgerInfo, (info, index) => (
                        <tr key={index}>
                          <TableCell>{info.sku}</TableCell>
                          <TableCell>{info.imei}</TableCell>
                          <TableCell>{info.iccid}</TableCell>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </ColumnBox>

          <AccordionContainer>
            <Accordion>
              <AccordionSummary>
                <h3>ShipStation Shipment</h3>
              </AccordionSummary>

              <AccordionDetails>
                <FormattedCode.FC content={queryTuple.data.rawShipStationShipment} />
              </AccordionDetails>
            </Accordion>
          </AccordionContainer>

          <AccordionContainer>
            <Accordion>
              <AccordionSummary>
                <h3>47 File</h3>
              </AccordionSummary>

              <AccordionDetails>
                <h3>Header Row</h3>
                <TableContainerBox>
                  <Table>
                    <thead>
                      <tr>
                        {_.map(queryTuple.data.headerKeys, key => (
                          <TableCell key={key}>
                            {key}
                          </TableCell>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      <tr>
                        {_.map(queryTuple.data?.headerKeys ?? [], key => (
                          <TableCell key={key}>
                            {queryTuple.data.headerKey2Value[key] ?? ''}
                          </TableCell>
                        ))}
                      </tr>
                    </tbody>
                  </Table>
                </TableContainerBox>

                <h3>Address Row</h3>
                <TableContainerBox>
                  <Table>
                    <thead>
                      <tr>
                        {_.map(queryTuple.data.addressKeys, key => (
                          <TableCell key={key}>
                            {key}
                          </TableCell>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      <tr>
                        {_.map(queryTuple.data?.addressKeys ?? [], key => (
                          <TableCell key={key}>
                            {queryTuple.data.addressRow2Value[key] ?? ''}
                          </TableCell>
                        ))}
                      </tr>
                    </tbody>
                  </Table>
                </TableContainerBox>

                <h3>Detail Rows</h3>
                <TableContainerBox>
                  <Table>
                    <thead>
                      <tr>
                        {_.map(queryTuple.data.detailKeys, key => (
                          <TableCell key={key}>
                            {key}
                          </TableCell>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {_.map(queryTuple.data.detailRowsRow2Value ?? [], (row, index) => (
                        <tr key={index}>
                          {_.map(queryTuple.data?.detailKeys ?? [], key => (
                            <TableCell key={key}>
                              {row[key] ?? ''}
                            </TableCell>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableContainerBox>
              </AccordionDetails>
            </Accordion>
          </AccordionContainer>

          <AccordionContainer>
            <Accordion>
              <AccordionSummary>
                <h3>Source Order</h3>
              </AccordionSummary>

              <AccordionDetails>
                <FormattedCode.FC content={queryTuple.data.rawSourceOrder} />
              </AccordionDetails>
            </Accordion>
          </AccordionContainer>

          <AccordionContainer>
            <Accordion>
              <AccordionSummary>
                <h3>ShipStation Order</h3>
              </AccordionSummary>

              <AccordionDetails>
                <FormattedCode.FC content={queryTuple.data.rawShipStationOrder} />
              </AccordionDetails>
            </Accordion>
          </AccordionContainer>

          {queryTuple.data.smartPayApplicationNumber && params.orderId && (
            <AccordionContainer>
              <SmartPayLeaseApplicationSection.FC orderId={params.orderId} />
            </AccordionContainer>
          )}
        </Section.FC>
      )}
    </activityTemplates.BaseActivityTemplate.FC >
  );
};
